import { Link } from 'react-router-dom';
import { featurePillars, workflowHighlights } from '../lib/marketing';

export function LandingPage(): JSX.Element {
  const spotlightFeatures = featurePillars.slice(0, 3);
  const supportingFeatures = featurePillars.slice(3);

  return (
    <div className="space-y-24">
      <section className="glass-card relative overflow-hidden px-6 py-14 sm:px-12 sm:py-20">
        <div
          className="absolute inset-y-[-35%] right-[-20%] h-[420px] w-[420px] rounded-full bg-[radial-gradient(circle_at_center,hsl(237_86%_72%/.55),transparent_65%)] blur-3xl dark:bg-[radial-gradient(circle_at_center,hsl(212_92%_62%/.5),transparent_65%)]"
          aria-hidden
        />
        <div
          className="absolute inset-y-[45%] left-[-25%] h-[360px] w-[360px] rounded-full bg-[radial-gradient(circle_at_center,hsl(189_88%_68%/.45),transparent_65%)] blur-2xl dark:bg-[radial-gradient(circle_at_center,hsl(286_86%_66%/.4),transparent_65%)]"
          aria-hidden
        />

        <div className="relative grid gap-12 lg:grid-cols-[1.1fr_0.9fr] lg:items-center">
          <div className="space-y-6">
            <span className="badge-soft">Introducing InspectAI v1.0</span>
            <h1 className="text-4xl font-semibold tracking-tight text-foreground sm:text-5xl">
              Luxury-grade inspection reports crafted in minutes.
            </h1>
            <p className="text-lg text-muted-foreground sm:max-w-xl">
              Elevate every property walkthrough with AI-guided photo capture, instant condition scoring, and a
              glassmorphism studio that feels as premium as the spaces you represent.
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <Link to="/signup" className="btn-glass btn-primary focus-ring text-sm">
                Start free inspection
              </Link>
              <Link to="/features" className="btn-glass btn-secondary focus-ring text-sm">
                Watch product tour
              </Link>
            </div>
            <p className="text-xs uppercase tracking-[0.25em] text-muted-foreground">
              Includes one complimentary export • No credit card required
            </p>
          </div>

          <div className="surface-panel relative overflow-hidden p-6 text-sm shadow-lg">
            <div
              className="float-soft absolute -right-24 -top-24 h-60 w-60 rounded-full bg-[radial-gradient(circle_at_center,hsl(278_94%_74%/.4),transparent_70%)] dark:bg-[radial-gradient(circle_at_center,hsl(186_90%_58%/.28),transparent_70%)]"
              aria-hidden
            />
            <div className="relative space-y-4">
              <div className="rounded-2xl border border-white/20 bg-white/60 p-4 shadow-inner dark:border-white/10 dark:bg-white/10">
                <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Live AI summary</p>
                <p className="mt-2 text-sm text-foreground">
                  “Penthouse kitchen presents luxury finishes. Two cosmetic scuffs flagged for concierge follow-up. Recommend
                  polish and repaint before next lease rotation.”
                </p>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="rounded-2xl border border-white/30 bg-white/70 px-4 py-3 text-sm text-foreground dark:border-white/10 dark:bg-white/10">
                  <p className="text-xs text-muted-foreground">Room readiness</p>
                  <p className="text-lg font-semibold">96%</p>
                </div>
                <div className="rounded-2xl border border-white/30 bg-white/70 px-4 py-3 text-sm text-foreground dark:border-white/10 dark:bg-white/10">
                  <p className="text-xs text-muted-foreground">Issues detected</p>
                  <p className="text-lg font-semibold text-warning">3 minor</p>
                </div>
              </div>
              <div className="rounded-2xl border border-white/20 bg-white/60 p-4 text-xs text-muted-foreground dark:border-white/10 dark:bg-white/10">
                <p className="font-semibold text-foreground">Concierge checklist</p>
                <ul className="mt-2 space-y-1">
                  <li>• Capture balcony panorama before sunset</li>
                  <li>• Verify valet access with concierge team</li>
                  <li>• Prepare onboarding kit for new resident</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {['Harbor & Co.', 'Vista Residences', 'Aurora Living', 'LuxeStay Collective'].map((brand) => (
            <div key={brand} className="glass-card flex items-center justify-center px-4 py-3 text-xs font-semibold uppercase tracking-[0.4em] text-muted-foreground">
              {brand}
            </div>
          ))}
        </div>
      </section>

      <section className="space-y-10">
        <div className="section-accent relative">
          <div className="relative flex flex-col gap-4 text-left sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h2 className="text-3xl font-semibold text-foreground">Signature moments, intelligently orchestrated</h2>
              <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
                InspectAI choreographs every step of the inspection journey— from immersive capture to curated storytelling— so your
                team can deliver boutique-level reporting at scale.
              </p>
            </div>
            <Link to="/features" className="btn-glass btn-secondary focus-ring text-sm">
              Explore the platform
            </Link>
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          {spotlightFeatures.map((pillar) => (
            <article key={pillar.title} className="glass-card flex h-full flex-col gap-4 p-6">
              <h3 className="text-lg font-semibold text-foreground">{pillar.title}</h3>
              <p className="text-sm text-muted-foreground">{pillar.description}</p>
              <ul className="space-y-2 text-sm text-muted-foreground">
                {pillar.bullets.map((bullet) => (
                  <li key={bullet}>• {bullet}</li>
                ))}
              </ul>
            </article>
          ))}
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          {supportingFeatures.map((pillar) => (
            <article key={pillar.title} className="surface-panel p-6 text-sm text-muted-foreground dark:text-foreground">
              <h3 className="text-base font-semibold text-foreground">{pillar.title}</h3>
              <p className="mt-2 text-sm">{pillar.description}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="glass-card space-y-3 p-6 text-sm text-muted-foreground">
        <h2 className="text-lg font-semibold text-foreground">Design-led workflow</h2>
        <p>The InspectAI rhythm keeps your team in sync:</p>
        <ol className="grid gap-3 sm:grid-cols-5">
          {workflowHighlights.map((step, index) => (
            <li
              key={step}
              className="rounded-2xl border border-white/20 bg-white/60 px-4 py-3 text-xs font-semibold text-muted-foreground dark:border-white/10 dark:bg-white/10"
            >
              <span className="block text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Step {index + 1}</span>
              <span className="mt-2 block text-sm text-foreground">{step}</span>
            </li>
          ))}
        </ol>
      </section>

      <section className="glass-card grid gap-8 px-8 py-10 sm:grid-cols-[1.5fr_1fr]">
        <div className="space-y-4">
          <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Client highlight</p>
          <blockquote className="text-lg leading-relaxed text-foreground">
            “Our luxury leasing team produces inspection packs in record time. Clients love the elevated aesthetic— it feels bespoke,
            yet our process is faster than ever.”
          </blockquote>
          <p className="text-sm font-semibold text-foreground">Jasmine Clarke · Director of Guest Experience, Harbor & Co.</p>
        </div>
        <div className="surface-panel flex flex-col justify-between rounded-3xl p-6 text-sm text-muted-foreground">
          <div>
            <p className="text-xs uppercase tracking-[0.3em]">What you get</p>
            <ul className="mt-3 space-y-2">
              <li>• Complimentary onboarding session</li>
              <li>• Concierge success channel</li>
              <li>• Advanced AI templates and shareable filters</li>
            </ul>
          </div>
          <Link to="/pricing" className="btn-glass btn-primary focus-ring mt-6 self-start text-sm">
            View membership plans
          </Link>
        </div>
      </section>

      <section className="glass-card flex flex-col items-center gap-6 px-8 py-12 text-center">
        <span className="badge-soft">Ready when you are</span>
        <h2 className="max-w-2xl text-3xl font-semibold text-foreground sm:text-4xl">
          Bring a couture experience to every inspection.
        </h2>
        <p className="max-w-2xl text-sm text-muted-foreground">
          Sign up free, upload your first property, and let InspectAI orchestrate the storytelling. Upgrade for unlimited reports,
          collaboration, and concierge-level support.
        </p>
        <div className="flex flex-wrap items-center justify-center gap-3">
          <Link to="/signup" className="btn-glass btn-primary focus-ring text-sm">
            Launch free trial
          </Link>
          <a
            href="mailto:raykundan57@gmail.com"
            target="_blank"
            rel="noreferrer"
            className="btn-glass btn-secondary focus-ring text-sm"
          >
            Talk to our team
          </a>
        </div>
      </section>
    </div>
  );
}
